import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  DialogActions,
  Button,
  Divider,
  Grid2,
  Stack,
  Card,
  Chip,
  LinearProgress,
} from '@mui/material';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { DateTime } from 'luxon';
import API from '../../api/api';
import { useDispatch } from 'react-redux';
import { setSnackError } from '../../Reducers/snackErrors';
import { Stars, StarsOutlined } from '@mui/icons-material';

const StudentInfoSession = ({ sessionInfo, assessmentInfo, open, close }) => {

  const dispatch = useDispatch();
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  useEffect(() => {
    console.log("Re-render SessionInfo");
  }, [sessionInfo, assessmentInfo]);


  const closeDialog = () => {
    setSelectedSession(null);
    setSelectedAssessment(null);
    close();
  }


  const handleSessionClick = (row) => {
    setSelectedAssessment(null);
    setSelectedSession(row);
  } 

  const handleAssessmentSearch = async (row) => {
    setSelectedAssessment(null);
    setSelectedSession(null);
    try {
      const response = await API.get('/student_assessment_search', {params: {student_assessment_id: row.id, easy_score: false}});
      if (response.data.data) {
        setSelectedAssessment(response.data.data);
      }
      else {
        setSelectedAssessment(null);
        setSelectedSession(null);
      }
    }
    catch(error) {
        if (error.isAuthError || error.isForbidden) {
            return;
          } 
          console.log(error)
          if (error.isNetworkError) {
            dispatch(setSnackError({ 
              title: 'Connection Error', 
              body: error.message 
            }));
          } else {
            dispatch(setSnackError({
              title: 'Error found',
              body: error.message || 'Unable to create session.'
            }));
          }
        return;
    }
  }


  // Reusable DetailItem component
  function DetailItem({ label, value, multiline = false }) {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        {multiline ? (
          <Typography variant="body2" whiteSpace="pre-wrap">
            {value}
          </Typography>
        ) : (
          <Typography variant="body1" noWrap>
            {value}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Dialog sx={{maxHeight: '80vh'}} scroll="paper" open={open} onClose={close} fullWidth maxWidth="xl">
      <DialogTitle>
        <Typography variant="h6" fontWeight={'bold'}>Session Information</Typography>
        <Typography variant="body2" gutterBottom>
                Select session to view session details
          </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
      <Grid2 container spacing={2}>
        <Grid2 size={{xs: 12, md: 6}}>
          <Box sx={{ borderRadius: 0, overflow: 'hidden' }}>
            <Typography fontWeight={'bold'} variant='subtitle1' gutterBottom>
                Sessions (all)
            </Typography> 
            <Stack spacing={1}>
            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Session date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tutor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Program</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionInfo && [...sessionInfo].sort((a, b) => new Date(a.session_date) - new Date(b.session_date)).map((row) => (
                    <TableRow
                      key={`${row.id}-${row.period}`}
                      hover
                      selected={selectedSession?.id === row.id}
                      onClick={() => handleSessionClick(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {DateTime.fromISO(row.session_date, { zone: 'utc' }).toLocaleString(DateTime.DATE_SHORT)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography 
                          variant="body2" 
                          display="flex" 
                          justifyContent="center" 
                          alignItems="center"
                          gap={0.5}
                        >
                          {row.substitute && row.substitute_name}
                          {row.substitute && row.substitute_name && <PublishedWithChangesIcon fontSize="small" />}
                          {`${row.first_name} ${row.last_name}`}
                          
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {row.program_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{DateTime.fromFormat(row.start_time, "HH:mm:ss").toFormat("hh:mm a")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{pt: 1, pb: 1}} />

            <Typography fontWeight={'bold'} variant='subtitle1'>
                Assessments (all)
            </Typography> 

            <TableContainer sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Session date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Score</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Max</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessmentInfo && [...assessmentInfo].sort((a,b) => new Date(a.session_date) - new Date(b.session_date))
                    .map((assessment, index) => (
                      <TableRow 
                      onClick={async() => handleAssessmentSearch(assessment)}
                      hover sx={{ cursor: 'pointer' }}
                      key={assessment.id}>
                        <TableCell>
                          {DateTime.fromISO(assessment.session_date).toLocaleString(DateTime.DATE_SHORT)}
                        </TableCell>
                        <TableCell>{assessment.title}</TableCell>
                        <TableCell align="right">{assessment.score.toFixed(2)}</TableCell>
                        <TableCell align="right">{assessment.max_score}</TableCell>
                        <TableCell align="right">
                        <Typography variant='caption'>
                        { ((assessment.score/assessment.max_score)*100).toFixed(2) + "%"}
                        <LinearProgress color='secondary' variant="determinate" value={((assessment.score/assessment.max_score)*100).toFixed(2)} />
                        </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Stack>
            {!sessionInfo?.length && (
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  No sessions found
                </Typography>
              </Box>
            )}
          </Box>
        </Grid2>
        {/* Right: Session Details */}
        <Grid2 size={{xs: 12, md: 6}}>
          <Card elevation={0} sx={{ p: 2, height: '100%', borderRadius: 2 }}>
            {
              selectedAssessment ? (
                <Box sx={{ mb: 3, overflowY: 'auto' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Assessment Details
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    View assessment details per selected data point.
                  </Typography>
                  <Divider />
                
                  <Grid2 container spacing={2}>
                    {selectedAssessment.map((item, index) => {
                     return (
                      <>
                      <Grid2 size={{xs:12}}>
                        <DetailItem label={`Question: ${index + 1} - ${item.question_type}`} value={item.question} multiline={true} />
                      </Grid2>
                      <Grid2 size={{xs:12}}>                
                        { item.choice_text && 
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2" color="secondary">
                              {"Response"}
                            </Typography>
                            <Typography variant="body1" noWrap>
                                {item.choice_text}
                              </Typography>
                          </Box> }
                        { item.answer_text && 
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" color="secondary">
                            {"Student Answer"}
                          </Typography>
                          <Typography variant="body1">
                              {item.answer_text}
                            </Typography>
                        </Box> }
                          
                        { item.feedback && 
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" color="success">
                            {"Feedback"}
                          </Typography>
                          <Typography variant="body1">
                              {item.feedback}
                            </Typography>
                        </Box> }
                        <Stack direction={'row'} spacing={1}>
                          <Chip label={`Correct: ${item.is_correct}`} color='primary' variant='filled' />
                          <Chip label={`Points: ${item.answer_points}`} color='secondary' variant='outlined' />    
                          <Chip label={`Max Points: ${item.points}`} color='secondary' variant='outlined' />  
                        </Stack>   
                      </Grid2>
                      <Divider />
                      </>
                    )
                    })}
                  </Grid2>

                 
                </Box>
              ): null
            }
            { selectedSession ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Session Details
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    View session details per selected session data point.
                  </Typography>
                  <Divider />
                
                  <Grid2 container spacing={2}>
                  <Grid2 size={{xs:6}}>
                      <DetailItem label="Program" value={selectedSession.program_name} />
                      <DetailItem label="Date" value={
                        DateTime.fromISO(selectedSession.session_date, {zone: 'utc'}).toFormat("yyyy-MM-dd")
                      } />
                      <DetailItem label="Student absent" value={`${selectedSession.absent}`} />

                    </Grid2>
                    <Grid2 size={{xs:6}}>
                      <DetailItem label="Time" value={selectedSession.start_time} />
                      {selectedSession.timeframe ? 
                      <DetailItem label="Session timeframe" value={`${selectedSession.timeframe_start}-${selectedSession.timeframe_end}`} />
                      :
                      <DetailItem label="Session duration" value={`${selectedSession.session_duration} mins`} />
                      }
                      <DetailItem label="Student duration" value={`${selectedSession.student_duration} mins`} />

                      <DetailItem label="Session size" value={`${selectedSession.student_count}`} />

                      <DetailItem label="Substitue" value={`${selectedSession.substitute}`} />
                    </Grid2>
                  </Grid2>

                  <DetailItem 
                    label="Notes" 
                    value={selectedSession.notes || 'No notes available'} 
                    multiline
                  />
                </Box>
              </>
            ) : (
             null
            )}
          </Card>
        </Grid2>
      </Grid2>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="text">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// PropTypes for validation
StudentInfoSession.propTypes = {
  sessionInfo: PropTypes.array,
  assessmentInfo: PropTypes.array.isRequired,
  open: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};

export default StudentInfoSession;